import { ProductStatus } from '@core/enums/product-status.enum';
import { Plan } from '@core/interfaces/plan/plan.interface';
import { IIdentity } from '@core/interfaces/util/identity.interface';
import { IAppTableRow } from '@shared/modules/table/interfaces/table-row.interface';

import { TProductType } from '../../../../../th-mjc/app/core/enums/product-type.enum';
import { BaseConsumer } from '../claims/claimDetails.interface';

export interface QuickSearchConsumer extends BaseConsumer {
  myServiceAgreements: IQuickSearchPurchase[];
  protectionPlans: IQuickSearchPlansPurchase[];
  plans?: Plan[];
  planRows?: IAppTableRow[];
}

export interface IQuickSearchPurchase {
  coveredProducts: IQuickSearchProduct[];
  id: number;
  name: string;
  purchaseDate: string;
  purchaseRetailerId: number;
  purchaseRetailerName: string;
  receiptNumber: string;
  servicingRetailerId: number;
  servicingRetailerName: string;
  otherClaims: IQuickSearchClaim[];
}

export interface IQuickSearchPlansPurchase {
  id: string;
  purchaseDate: string;
  receiptNumber: string;
  plans: IQuickSearchPlan[];
}

export interface IQuickSearchPlan {
  accessiblePlan: boolean;
  coveredProducts: IQuickSearchProduct[];
  name: string;
  parentPlanName: string;
  purchaseRetailerId: number;
  purchaseRetailerName: string;
  servicingRetailerId: number;
  servicingRetailerName: string;
  id?: number;
  otherClaims?: IQuickSearchClaim[];
}

export interface IQuickSearchProduct {
  id: string;
  name: string;
  claims: IQuickSearchClaim[];
}

export interface IQuickSearchClaim {
  id: number;
  claimNumber: string;
  dateOpen: string;
}

export interface Purchase {
  claims: PurchaseClaim[];
  coveredProducts: Product[];
  deliveryDate: Date;
  id: number;
  name: string;
  purchaseDate: string;
  receiptNumber: string;
  retailerName: string;
  consumerAgreement: string;
  purchaseRetailerName: string;
  servicingRetailerName: string;
  purchaseRetailerId: number;
  retailerId: number;
  servicingRetailerId: number;
}

export interface Product {
  deliveryDate: string;
  id: string;
  crmRefId: string;
  manufacturerName: string;
  manufacturerAccount: IIdentity<string>;
  name: string;
  productName: string;
  serialNumber: string;
  sku: string;
  coveredProductStatus: ProductStatus;
  coveredProductStatusName: string;
  disabled?: boolean;
  vendor?: string;
  productMaterialType?: number;
  mjcProductType?: TProductType;
  mjcProductTypeName?: string;
}

const PRODUCT_STATUS_NAME: Record<ProductStatus, string> = {
  [ProductStatus.Active]: 'Available',
  [ProductStatus.PendingVerification]: 'Available',
  [ProductStatus.PartiallyRefunded]: 'Partially Refunded',
  [ProductStatus.FullyRefunded]: 'Fully Refunded',
  [ProductStatus.Voided]: 'Voided',
  [ProductStatus.Expired]: 'Expired',
  [ProductStatus.Replaced]: 'Replaced',
  [ProductStatus.Fulfilled]: 'Fulfilled',
  [ProductStatus.PackageItemOnly]: 'Package Item Only (not for servicing)',
  [ProductStatus.CashSettled]: 'Cash Settled',
};

export function getProductStatusName(status: ProductStatus): string {
  return PRODUCT_STATUS_NAME[status];
}

export interface PurchaseClaim {
  claimNumber: string;
  claimStatus: string;
  dateClosed: Date;
  dateOpen: Date;
  id: number;
  incidents: Incidents[];
}

export interface Incidents {
  coveredProductId: string;
  id: string;
  problemCause: string;
  problemDescription: string;
}
